import React, { createContext, useContext, useState, ReactNode } from 'react';

interface SurveyConfig {
  companySize: string;
  industry: string;
  focusAreas: string[];
  questionCount: number;
  questionTypes: string[];
  surveyFrequency: string;
  targetAudience: string;
  additionalContext: string;
  email?: string; // captured for anonymous generations
}

interface GeneratedQuestion {
  id: string;
  type: string;
  category: string;
  question: string;
  options?: string[];
  scale?: { min: number; max: number; labels?: string[] };
}

interface SurveyContextType {
  config: SurveyConfig;
  setConfig: React.Dispatch<React.SetStateAction<SurveyConfig>>;
  generatedQuestions: GeneratedQuestion[];
  setGeneratedQuestions: React.Dispatch<React.SetStateAction<GeneratedQuestion[]>>;
  isGenerating: boolean;
  setIsGenerating: React.Dispatch<React.SetStateAction<boolean>>;
}

const defaultConfig: SurveyConfig = {
  companySize: '',
  industry: '',
  focusAreas: [],
  questionCount: 10,
  questionTypes: [],
  surveyFrequency: '',
  targetAudience: '',
  additionalContext: '',
  email: ''
};

const SurveyContext = createContext<SurveyContextType | undefined>(undefined);

export const SurveyProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [config, setConfig] = useState<SurveyConfig>(defaultConfig);
  const [generatedQuestions, setGeneratedQuestions] = useState<GeneratedQuestion[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);

  return (
    <SurveyContext.Provider
      value={{
        config,
        setConfig,
        generatedQuestions,
        setGeneratedQuestions,
        isGenerating,
        setIsGenerating,
      }}
    >
      {children}
    </SurveyContext.Provider>
  );
};

export const useSurvey = () => {
  const context = useContext(SurveyContext);
  if (context === undefined) {
    throw new Error('useSurvey must be used within a SurveyProvider');
  }
  return context;
};

export type { SurveyConfig, GeneratedQuestion };